import { Button, Checkbox, Grid, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getOrderAmountDue, isApprovedPaymentLine } from '@washday/utilities';
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';
import UpdateOrderForm from '../../components/OrderModals/UpdateOrderForm';
import BulkUpdateOrderForm, {
  BulkOrderSelection
} from '../../components/OrderModals/BulkUpdateOrderForm';
import BulkOrderAddPaymentLineModal from '../../components/OrderModals/BulkOrderAddPaymentLineModal';
import { IOrder } from '../../interfaces/Order';
import { IStore } from '../../interfaces/Store';
import { ICompany } from '../../interfaces/Company';
import { IUser } from '../../interfaces/User';
import { PaymentMethodsEnum } from '../../enum/PaymentMethodsEnum';
import { getOrdersListV2 } from '../../services/order.service';
import { showNotification } from '../../utils/Notification/Notification';
import { useGetCashierboxes } from '../cashierboxes/queries';
import { useOrderCollectionActions } from './useOrderCollectionActions';
import {
  useBulkSetOrderCancelled,
  useBulkSetOrderCleaned,
  useBulkSetOrderCollected,
  useSetOrderCancelled,
  useSetOrderCleaned
} from './mutations';

interface OrderStatusUpdateFlowOptions {
  storeId: string;
  status: 'cleaning' | 'ready';
  store?: IStore;
  assignedEmployee?: string;
  assignedEmployeeLabel?: string;
  contextKey?: string;
  enabled?: boolean;
  onUpdated?: () => unknown | Promise<unknown>;
}
interface UpdateSession {
  key: string;
  controller: AbortController;
}
class SelectionChanged extends Error {}

/** Shared Cleaning/Ready header flow. Each instance owns only its own dialogs. */
export function useOrderStatusUpdateFlow({
  storeId,
  status,
  store,
  assignedEmployee,
  assignedEmployeeLabel,
  contextKey,
  enabled = true,
  onUpdated
}: OrderStatusUpdateFlowOptions) {
  const { t } = useTranslation(['UpdateOrderForm', 'CommonButtons', 'Notifications']);
  const { company, user } = useSelector(
    (state: { user: { company?: ICompany; user?: Pick<IUser, 'cashierBox'> } }) => state.user
  );
  const { data: cashierboxes } = useGetCashierboxes(!!storeId, storeId);
  const collection = useOrderCollectionActions({ store, company });
  const cleaned = useSetOrderCleaned();
  const cancelled = useSetOrderCancelled();
  const bulkCleaned = useBulkSetOrderCleaned();
  const bulkCancelled = useBulkSetOrderCancelled();
  const bulkCollected = useBulkSetOrderCollected();
  const instanceId = useId();
  const modalIds = useMemo(
    () => ({
      individual: `${instanceId}:updateOrderModal`,
      bulk: `${instanceId}:bulkUpdateOrderModal`,
      payment: `${instanceId}:bulkPostPaymentLines`,
      confirmation: `${instanceId}:confirmation`,
      pin: `${instanceId}:pin`
    }),
    [instanceId]
  );
  const scopeKey = JSON.stringify([storeId, status, assignedEmployee, contextKey]);
  const latest = useRef({ scopeKey, enabled, storeId });
  latest.current = { scopeKey, enabled, storeId };
  const session = useRef<UpdateSession | null>(null);
  const busy = useRef(false);
  const paymentSubmitting = useRef(false);
  const [isPending, setPending] = useState(false);
  const closeOwnedDialogs = useCallback(
    () => Object.values(modalIds).forEach((id) => modals.close(id)),
    [modalIds]
  );
  useEffect(
    () => () => {
      session.current?.controller.abort();
      session.current = null;
      closeOwnedDialogs();
    },
    [scopeKey, enabled, closeOwnedDialogs]
  );

  const isCurrent = (target: UpdateSession) =>
    session.current === target &&
    !target.controller.signal.aborted &&
    latest.current.enabled &&
    latest.current.scopeKey === target.key;
  const assertCurrent = (target: UpdateSession, selection?: BulkOrderSelection) => {
    if (
      !isCurrent(target) ||
      (selection?.storeId && selection.storeId !== storeId) ||
      selection?.isSelectionCurrent?.() === false
    )
      throw new SelectionChanged();
  };
  const readOrder = async (target: UpdateSession, sequence: string, expectedId?: string) => {
    assertCurrent(target);
    const response = await getOrdersListV2({
      storeId,
      status,
      assignedEmployee,
      sequence,
      pageNum: 0,
      limit: 1,
      sequenceSort: '-1',
      fetchSection: 'true',
      includeAmountDueTotal: false,
      signal: target.controller.signal
    });
    assertCurrent(target);
    const order = response?.data?.data?.orders?.[0] as IOrder | undefined;
    if (
      !order?._id ||
      String(order.sequence) !== String(sequence) ||
      (expectedId && order._id !== expectedId) ||
      (order.status && order.status !== status) ||
      (order.createdIn &&
        (typeof order.createdIn === 'string' ? order.createdIn : order.createdIn._id) !== storeId)
    )
      throw new SelectionChanged();
    return order;
  };
  const validateSelection = async (target: UpdateSession, selection: BulkOrderSelection) => {
    assertCurrent(target, selection);
    const ids = [...new Set(selection.ordersIds)];
    if (!ids.length || ids.length !== selection.selectedOrders.length) throw new SelectionChanged();
    const orders: IOrder[] = [];
    for (const id of ids) {
      const selected = selection.selectedOrders.find((order) => order._id === id);
      if (selected?.sequence === undefined) throw new SelectionChanged();
      orders.push(await readOrder(target, String(selected.sequence), id));
      assertCurrent(target, selection);
    }
    return orders;
  };
  const run = async (target: UpdateSession, action: () => Promise<unknown>) => {
    if (!isCurrent(target) || busy.current) return false;
    busy.current = true;
    setPending(true);
    try {
      await action();
      return true;
    } catch (error) {
      if (error instanceof SelectionChanged || !isCurrent(target)) {
        if (isCurrent(target))
          showNotification({ type: 'Error', title: t('UpdateOrderForm:errors.selectionChanged') });
        return false;
      }
      showNotification({ type: 'Error', title: t('Notifications:somethingWrong') });
      throw error;
    } finally {
      busy.current = false;
      setPending(paymentSubmitting.current);
    }
  };
  const completed = async () => {
    showNotification({ type: 'Default', title: t('Notifications:success') });
    await onUpdated?.();
  };
  const submitBulkCollection = async (
    target: UpdateSession,
    selection: BulkOrderSelection,
    collectWithAmountDue = false
  ) => {
    await validateSelection(target, selection);
    const pinUser = await collection.onAskForPin({
      storeId,
      signal: target.controller.signal,
      modalId: modalIds.pin
    });
    await validateSelection(target, selection);
    const result = await bulkCollected.mutateAsync({
      storeId,
      body: {
        ordersIds: selection.ordersIds,
        ...(pinUser?._id ? { pinUserId: pinUser._id } : {}),
        ...(pinUser?.pinValidationToken ? { pinValidationToken: pinUser.pinValidationToken } : {}),
        ...(collectWithAmountDue ? { collectWithAmountDue: true } : {}),
        collectedDateTime: new Date().toISOString()
      }
    });
    await completed();
    return result;
  };
  const openPayments = (
    target: UpdateSession,
    selection: BulkOrderSelection,
    collectAfter: boolean
  ) =>
    run(target, async () => {
      const selectedOrders = await validateSelection(target, selection);
      const initialCashierBox =
        typeof user?.cashierBox === 'string' &&
        cashierboxes?.some((box) => box._id === user.cashierBox)
          ? user.cashierBox
          : cashierboxes?.[0]?._id || '';
      const paymentLines = selectedOrders.map((order) => ({
        sequence: String(order.sequence),
        folioPrefix: order.folioPrefix,
        orderId: order._id!,
        paymentMethod: PaymentMethodsEnum.Card,
        amount: getOrderAmountDue({
          ...order,
          paymentLines: order.paymentLines?.filter(isApprovedPaymentLine)
        }),
        cashierBoxId: initialCashierBox,
        paymentDate: new Date()
      }));
      modals.close(modalIds.bulk);
      modals.open({
        modalId: modalIds.payment,
        title: t('UpdateOrderForm:title'),
        children: (
          <BulkOrderAddPaymentLineModal
            close={() => modals.close(modalIds.payment)}
            storeId={storeId}
            paymentLines={paymentLines}
            disableAmountField={collectAfter}
            beforeSubmit={async (lines) => {
              try {
                const currentOrders = await validateSelection(target, selection);
                if (
                  collectAfter &&
                  lines.some((line) => {
                    const order = currentOrders.find((order) => order._id === line.orderId)!;
                    return (
                      getOrderAmountDue({
                        ...order,
                        paymentLines: order.paymentLines?.filter(isApprovedPaymentLine)
                      }) !== line.amount
                    );
                  })
                )
                  throw new SelectionChanged();
                assertCurrent(target, selection);
                return true;
              } catch {
                if (isCurrent(target))
                  showNotification({
                    type: 'Error',
                    title: t('UpdateOrderForm:errors.selectionChanged')
                  });
                return false;
              }
            }}
            onSubmittingChange={(value) => {
              paymentSubmitting.current = value;
              setPending(value || busy.current);
            }}
            onPaymentLinesCreated={async (successIds) => {
              await onUpdated?.();
              if (!collectAfter || !successIds.length) return;
              const successfulSelection = {
                ...selection,
                ordersIds: successIds,
                selectedOrders: selectedOrders.filter((order) => successIds.includes(order._id!))
              };
              return run(target, () => submitBulkCollection(target, successfulSelection));
            }}
          />
        )
      });
    });
  const openBulk = (target: UpdateSession) => {
    if (!isCurrent(target)) return;
    modals.close(modalIds.individual);
    const updateOptions = [
      {
        id: status === 'cleaning' ? 'markAsCleaned' : 'markAsCollected',
        label: t(
          `UpdateOrderForm:updateOrderOptions.${
            status === 'cleaning' ? 'markAsCleaned' : 'markAsCollected'
          }`
        ),
        onSubmit: (selection: BulkOrderSelection) => {
          if (!isCurrent(target) || selection.isSelectionCurrent?.() === false) return;
          let markOutsourcedAsReturned = Boolean(
            company?.companyServicesPermissions?.enableOutsourcing
          );
          const collectWithAmountDue =
            status === 'ready' &&
            !!store?.requireCashierPinToCollect &&
            selection.selectedOrders.some(
              (order) =>
                getOrderAmountDue({
                  ...order,
                  paymentLines: order.paymentLines?.filter(isApprovedPaymentLine)
                }) > 0
            );
          return ConfirmationModal({
            modalId: modalIds.confirmation,
            title: t(
              status === 'cleaning'
                ? 'Notifications:CRUD.bulkCleanedConfirmation'
                : 'Notifications:CRUD.bulkCollectedConfirmation'
            ),
            children:
              status === 'cleaning' && company?.companyServicesPermissions?.enableOutsourcing ? (
                <Checkbox
                  defaultChecked={markOutsourcedAsReturned}
                  label={t('Notifications:CRUD.markOutsourcedReturned')}
                  onChange={(event) => {
                    markOutsourcedAsReturned = event.target.checked;
                  }}
                />
              ) : collectWithAmountDue ? (
                <Text>{t('Notifications:CRUD.bulkCollectedWithAmountDueWarning')}</Text>
              ) : undefined,
            ...(collectWithAmountDue
              ? {
                  labels: {
                    confirm: t('Notifications:CRUD.bulkCollectedWithAmountDueConfirm'),
                    cancel: t('CommonButtons:cancel')
                  }
                }
              : {}),
            onConfirm: () =>
              run(target, async () => {
                if (status === 'cleaning') {
                  await validateSelection(target, selection);
                  await bulkCleaned.mutateAsync({
                    storeId,
                    body: {
                      ordersIds: selection.ordersIds,
                      cleanedDateTime: new Date().toISOString(),
                      readyDateTime: new Date().toISOString()
                    },
                    params: { markOutsourcedAsReturned }
                  });
                  await completed();
                } else await submitBulkCollection(target, selection, collectWithAmountDue);
                modals.close(modalIds.bulk);
              }),
            onCancel: () => {}
          });
        }
      },
      {
        id: 'createPaymentLines',
        label: t('UpdateOrderForm:updateOrderOptions.bulkCreatePaymentLines'),
        onSubmit: (selection: BulkOrderSelection) => openPayments(target, selection, false)
      },
      ...(status === 'ready'
        ? [
            {
              id: 'createPaymentLinesAndCollected',
              label: t('UpdateOrderForm:updateOrderOptions.bulkCreatePaymentLinesAndCollected'),
              onSubmit: (selection: BulkOrderSelection) => openPayments(target, selection, true)
            }
          ]
        : []),
      {
        id: 'masAsCancelled',
        label: t('UpdateOrderForm:updateOrderOptions.masAsCancelled'),
        onSubmit: (selection: BulkOrderSelection) => {
          if (!isCurrent(target)) return;
          return ConfirmationModal({
            modalId: modalIds.confirmation,
            title: t('Notifications:CRUD.bulkCancelConfirmation'),
            onConfirm: () =>
              run(target, async () => {
                await validateSelection(target, selection);
                await bulkCancelled.mutateAsync({
                  storeId,
                  body: {
                    ordersIds: selection.ordersIds,
                    cancelledDateTime: new Date().toISOString()
                  }
                });
                await completed();
                modals.close(modalIds.bulk);
              }),
            onCancel: () => {}
          });
        }
      }
    ];
    modals.open({
      modalId: modalIds.bulk,
      title: t('UpdateOrderForm:title'),
      children: (
        <>
          <BulkUpdateOrderForm
            storeId={storeId}
            orderStatusFilter={status}
            assignedEmployee={assignedEmployee}
            assignedEmployeeLabel={assignedEmployeeLabel}
            updateOptions={updateOptions}
            close={() => modals.close(modalIds.bulk)}
          />
        </>
      )
    });
  };
  const open = () => {
    if (!enabled || !storeId || store?._id !== storeId || busy.current || paymentSubmitting.current)
      return;
    session.current?.controller.abort();
    closeOwnedDialogs();
    const target = { key: scopeKey, controller: new AbortController() };
    session.current = target;
    const updateOptions = [
      {
        id: status === 'cleaning' ? 'markAsCleaned' : 'markAsCollected',
        label: t(
          `UpdateOrderForm:updateOrderOptions.${
            status === 'cleaning' ? 'markAsCleaned' : 'markAsCollected'
          }`
        ),
        onSubmit: ({
          sequence,
          params
        }: {
          sequence: string;
          params: { markOutsourcedAsReturned?: boolean };
        }) =>
          run(target, async () => {
            await readOrder(target, sequence);
            if (status === 'cleaning') {
              await cleaned.mutateAsync({
                sequence,
                storeId,
                data: {
                  cleanedDateTime: new Date().toISOString(),
                  readyDateTime: new Date().toISOString()
                },
                params
              });
              await completed();
            } else {
              const pinUser = await collection.onAskForPin({
                storeId,
                signal: target.controller.signal,
                modalId: modalIds.pin
              });
              await readOrder(target, sequence);
              await collection.onOrderCollected({
                sequence,
                storeId,
                pinUserId: pinUser?._id,
                ...(pinUser?.pinValidationToken
                  ? { pinValidationToken: pinUser.pinValidationToken }
                  : {})
              });
              await onUpdated?.();
            }
          })
      },
      {
        id: 'masAsCancelled',
        label: t('UpdateOrderForm:updateOrderOptions.masAsCancelled'),
        onSubmit: ({ sequence }: { sequence: string }) =>
          run(target, async () => {
            await readOrder(target, sequence);
            await cancelled.mutateAsync({
              sequence,
              storeId,
              data: { cancelledDateTime: new Date().toISOString() }
            });
            await completed();
          })
      }
    ];
    modals.open({
      modalId: modalIds.individual,
      styles: { title: { flex: 1, minWidth: 0 }, header: { alignItems: 'flex-start' } },
      title: (
        <Grid align="center" justify="flex-start" style={{ width: '100%', minWidth: 0 }}>
          <Grid.Col span={{ base: 12, sm: 5 }}>{t('UpdateOrderForm:title')}</Grid.Col>
          <Grid.Col span={{ base: 12, sm: 7 }}>
            <Button
              fullWidth
              color="var(--mantine-washday-secondary-color)"
              style={{ height: 'auto', minHeight: 36 }}
              styles={{ label: { whiteSpace: 'normal' } }}
              onClick={() => openBulk(target)}
            >
              {t('UpdateOrderForm:updateMultipleOrders')}
            </Button>
          </Grid.Col>
        </Grid>
      ),
      children: (
        <>
          <UpdateOrderForm
            updateOptions={updateOptions}
            storeId={storeId}
            orderFolioPrefix={store?.orderFolioPrefix}
            close={() => modals.close(modalIds.individual)}
          />
        </>
      )
    });
  };
  return { open, isPending };
}
